import { ApiClient } from '@/shared/api/client';
import type { AccountTeam, ApiToken, ApiTokenScope, ConsoleAccount, ConsoleMenu, IssuedToken } from '@/features/settings/types';

/**
 * API 토큰 — 콘솔 밖에서 API를 부르는 시스템(슬랙 봇 등)에 주는 자격증명.
 *
 * 프로젝트 보드가 아니라 설정에 있는 이유: 토큰은 특정 기능의 일부가 아니라 콘솔 밖 접근을 지키는
 * 수단이고, 지킬 경로가 늘어도 발급하는 자리는 하나여야 한다.
 */
export const apiTokenApi = {
  list: () => ApiClient.getJson<{ tokens: ApiToken[] }>('/api-tokens'),
  // 선택지는 서버가 정본이다 — 권한을 늘렸는데 화면에 안 뜨는 일이 없게.
  scopes: () => ApiClient.getJson<{ scopes: ApiTokenScope[] }>('/api-tokens/scopes'),
  issue: (label: string, scopes: string[]) => ApiClient.postJson<IssuedToken>('/api-tokens', { label, scopes }),
  // 평문은 그대로라 토큰을 쓰는 시스템의 시크릿을 바꾸지 않고 권한만 늘리고 줄일 수 있다.
  changeScopes: (id: number, scopes: string[]) => ApiClient.patchJson<ApiToken>(`/api-tokens/${id}`, { scopes }),
  revoke: (id: number) => ApiClient.deleteJson<{ revoked: true }>(`/api-tokens/${id}`),
};

/** 계정·팀·메뉴 권한. 로그인이 붙기 전까지는 저장만 되고 접근을 막지 않는다. */
export const accountApi = {
  // 체크박스 선택지는 서버가 정본이다 — 메뉴를 늘렸는데 화면에 안 뜨는 일이 없게.
  menus: () => ApiClient.getJson<{ menus: ConsoleMenu[] }>('/accounts/menus'),
  list: () => ApiClient.getJson<{ accounts: ConsoleAccount[] }>('/accounts'),
  create: (body: { email: string; name: string; teamId: number | null; menus: string[] }) =>
    ApiClient.postJson<ConsoleAccount>('/accounts', body),
  update: (id: number, body: Partial<{ name: string; teamId: number | null; menus: string[]; active: boolean }>) =>
    ApiClient.patchJson<ConsoleAccount>(`/accounts/${id}`, body),
  /** 비밀번호 설정 링크를 메일로 보낸다. 다시 보내면 이전 링크는 못 쓴다. */
  sendPasswordSetup: (id: number) => ApiClient.postJson<{ expiresAt: string }>(`/accounts/${id}/password-setup`),
  teams: () => ApiClient.getJson<{ teams: AccountTeam[] }>('/account-teams'),
  createTeam: (body: { name: string; defaultMenus: string[] }) => ApiClient.postJson<AccountTeam>('/account-teams', body),
  updateTeam: (id: number, body: Partial<{ name: string; defaultMenus: string[] }>) =>
    ApiClient.patchJson<AccountTeam>(`/account-teams/${id}`, body),
  removeTeam: (id: number) => ApiClient.deleteJson<{ removed: true }>(`/account-teams/${id}`),
};
